/**
 * The owner-review sign-in gate (docs/owner-signin-gate-plan.md, option D): on a gated surface
 * the ask says "Sign-in needed to publish.", the band says it again at step 1, and at step 3
 * Publish is the sign-in — the draft is kept, and the reader clicks Publish again once signed in
 * (the link still happens at Publish, #81 / 49cebd39). Creates only: an edit is never gated.
 * Frontend only: DealHunt still accepts a device-only publish (server enforcement is phase 2).
 *
 * No flag: the gate is on for every page surface that mounts the band (`GATE_SURFACES`) — the
 * flag was removed 2026-10-17 at Guillaume's request ("we don't need the flag env, it's noise"),
 * and slice D (2026-10-17) put the notice on the category, guide and review-post asks, so no
 * surface is left ungated. A constant, so the server HTML and the hydrated ask agree. Pure, no
 * JSX; tests `owner-signin-gate.spec.ts`.
 */
import { createContext } from 'react'

/**
 * Every page surface that mounts the band, one per GA4 event prefix (`product_page_*`,
 * `community_page_*`, `category_page_*`, `guide_page_*`, `review_page_*`), so a read-out splits
 * per event name. Constraint: every owner-review create is gated — a surface whose ask carries
 * no notice must not mount a band.
 */
export const GATE_SURFACES = ['product', 'community', 'category', 'guide', 'review'] as const
export type GateSurface = (typeof GATE_SURFACES)[number]

/**
 * Is this surface gated? Every band host and every ask calls this; same value on the server and
 * in the browser. True for every surface: a handed-off ask opens the community writer, which is
 * gated too, and every ask — inline or handed off — carries the notice.
 */
export function signInGateFor(surface: GateSurface): boolean {
  return (GATE_SURFACES as readonly string[]).includes(surface)
}

/** A band host wraps its band: `<SignInGateContext.Provider value={signInGateFor(…)}>`. The
 *  default (ungated) is left only to edit-only hosts (My gear), where the band never gates. */
export const SignInGateContext = createContext<boolean>(false)
